/**
 * Asking the proxy for a figure.
 *
 * Thin on purpose. Everything that can be got wrong without a network — building the request,
 * reading the reply, finding the script inside it — is in `model.ts` and on the server; what is
 * left here is one `fetch` and the discipline of never throwing at the caller.
 *
 * ## Why every failure is a message rather than an exception
 *
 * Because the caller is a panel with somewhere to put one. A thrown error from a click handler
 * reaches the error boundary and replaces the whole editor with a rescue screen — for a model
 * that was briefly unreachable, which is the wrong response by a wide margin.
 */

export type FigureResult =
  | { readonly script: string; readonly rounds: number }
  | { readonly error: string; readonly cancelled?: boolean };

/**
 * Where the API lives.
 *
 * Same origin by default, which is what a development server and a single-host deployment both
 * want: one path to route and no cross-origin rules to get right. `VITE_API_BASE` moves it, for
 * the deployment where the application is on a CDN and the API is at `api.hinhhoc.vn`.
 *
 * Read at build time rather than fetched at runtime. A runtime lookup would be one more request
 * before the first figure and one more thing to fail; the address of the API is a property of a
 * build, and it changes when a build changes.
 *
 * The trailing slash is trimmed so both `https://api.hinhhoc.vn` and `https://api.hinhhoc.vn/`
 * work. Whoever sets this is doing it once, in a deployment configuration, and should not have to
 * discover which of the two we meant.
 */
const BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '');
const ENDPOINT = `${BASE}/api/figure`;

export async function requestFigure(problem: string, signal?: AbortSignal): Promise<FigureResult> {
  let response: Response;
  try {
    response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ problem }),
      ...(signal === undefined ? {} : { signal }),
    });
  } catch {
    /*
     * A cancelled request lands here too, and it is not a failure to report as one. `fetch`
     * rejects on abort exactly as it does when the server is unreachable, so the two are told
     * apart by asking the signal rather than by reading the error — which is the only reliable
     * way across browsers, where the rejection is a `DOMException` in some and not in others.
     */
    if (signal?.aborted === true) return { error: 'Đã huỷ.', cancelled: true };
    return {
      error: 'Không gọi được máy chủ. Tính năng này cần proxy chạy kèm — xem README.',
    };
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    return { error: `Máy chủ trả về nội dung không đọc được (${String(response.status)}).` };
  }

  const body = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<
    string,
    unknown
  >;

  const script = body['script'];
  if (response.ok && typeof script === 'string' && script.trim() !== '') {
    // One when the first answer built, two when the endpoint sent the errors back. Defaulted
    // rather than demanded, so an older proxy in front of a newer app still draws.
    const rounds = body['rounds'];
    return { script, rounds: typeof rounds === 'number' ? rounds : 1 };
  }

  const message = body['error'];
  return {
    error:
      typeof message === 'string' && message !== ''
        ? message
        : `Máy chủ trả lỗi ${String(response.status)}.`,
  };
}
